import React from 'react';
import styled, { keyframes } from 'styled-components';
import {
  FaChalkboardTeacher, FaUserTie, FaUsers, FaBookOpen,
  FaTrophy, FaLaptopCode, FaHospital, FaHeadphones,
  FaComments, FaChartLine, FaBuilding, FaClipboardCheck,
} from 'react-icons/fa';

/* ─── Animations ─── */
const shimmer = keyframes`
  0%   { background-position: -200% center; }
  100% { background-position:  200% center; }
`;
const pulse = keyframes`
  0%, 100% { transform: scale(1); opacity: 1; }
  50%       { transform: scale(1.15); opacity: 0.7; }
`;
const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to   { opacity: 1; transform: translateY(0); }
`;

/* ─── Section ─── */
const Section = styled.section`
  background: #f8f7ff;
  position: relative;
  overflow: hidden;
  padding: 0;
  margin-top: 0;
  font-family: 'Times New Roman', serif;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background-image: repeating-linear-gradient(
      -45deg,
      rgba(55, 48, 163, 0.025) 0px,
      rgba(55, 48, 163, 0.025) 1px,
      transparent 1px,
      transparent 14px
    );
    pointer-events: none;
    z-index: 0;
  }
`;

/* ─── Hero Band (image left, text right) ─── */
const HeroBand = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  align-items: stretch;
  min-height: 480px;
  border-bottom: 1px solid rgba(55, 48, 163, 0.1);
  font-family: 'Times New Roman', serif;

  @media (max-width: 1366px) and (min-width: 769px) {
    min-height: 420px;
  }

  @media (max-width: 768px) {
    flex-direction: column;
    min-height: unset;
  }
`;

/* Left — image panel */
const HeroImagePanel = styled.div`
  flex: 1.3;
  background: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  position: relative;
  border-right: 1px solid rgba(55, 48, 163, 0.1);
  font-family: 'Times New Roman', serif;

  &::after {
    content: '';
    position: absolute;
    width: 450px; 
    height: 450px;
    border-radius: 50%;
    background: radial-gradient(circle, rgba(167, 139, 250, 0.06), transparent 70%);
    pointer-events: none;
    z-index: 0;
  }

  img {
    width: 100%;
    max-width: 700px;
    height: auto;
    object-fit: contain;
    position: relative;
    z-index: 1;
    image-rendering: auto;
    animation: ${fadeUp} 0.8s ease both;
  }

  @media (max-width: 1366px) and (min-width: 769px) {
    flex: 1.2;
    padding: 1.5rem;
    
    &::after {
      width: 350px; 
      height: 350px;
    }
    
    img {
      max-width: 600px;
    }
  }

  @media (max-width: 768px) {
    border-right: none;
    border-bottom: 1px solid rgba(55, 48, 163, 0.1);
    padding: 1.25rem 1rem;
    img { max-width: 300px; margin: 0 auto; }
  }
`;

/* Right — copy panel */
const HeroCopyPanel = styled.div`
  flex: 0.9;
  padding: 5rem 4rem;
  display: flex;
  flex-direction: column;
  justify-content: center;
  font-family: 'Times New Roman', serif;

  @media (max-width: 1366px) and (min-width: 769px) { 
    flex: 0.9;
    padding: 3rem 2.5rem; 
  }
  @media (max-width: 768px)  { 
    padding: 1.75rem 1.5rem; 
  }
`;

const EyebrowTag = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: #ede9fe;
  color: #3730a3;
  font-family: 'Times New Roman', serif;
  font-size: 1rem;
  font-weight: 800;
  letter-spacing: 2.5px;
  text-transform: uppercase;
  padding: 0.45rem 1rem;
  border-radius: 4px;
  margin-bottom: 1.8rem;
  width: fit-content;
  border-left: 3px solid #7c3aed;

  span {
    width: 5px; height: 5px;
    border-radius: 50%;
    background: #7c3aed;
    animation: ${pulse} 2.2s ease-in-out infinite;
  }

  @media (max-width: 768px) {
    font-size: 0.7rem;
    letter-spacing: 1.5px;
    margin-bottom: 1rem;
    padding: 0.4rem 0.8rem;
  }
`;

const HeroTitle = styled.h2`
  font-family: 'Times New Roman', serif;
  font-size: clamp(2.2rem, 4vw, 3.4rem);
  line-height: 1.15;
  font-weight: 900;
  color: #1a1a2e;
  margin: 0 0 1.4rem;

  em {
    font-family: 'Times New Roman', serif;
    font-style: normal;
    background: linear-gradient(100deg, #3730a3, #7c3aed, #a78bfa, #7c3aed);
    background-size: 200% auto;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    animation: ${shimmer} 5s linear infinite;
  }

  @media (max-width: 768px) {
    font-size: 1.9rem;
    margin: 0 0 1rem;
  }
`;

const HeroSub = styled.p`
  font-family: 'Times New Roman', serif;
  font-size: 1.05rem;
  color: #4a4a6a;
  line-height: 1.8;
  max-width: 420px;
  margin: 0;

  @media (max-width: 1366px) and (min-width: 769px) {
    font-size: 0.95rem;
  }

  @media (max-width: 768px) {
    font-size: 0.95rem;
    line-height: 1.6;
  }
`; 

/* ─── Divider Row ─── */
const DividerRow = styled.div`
  position: relative;
  z-index: 1;
  padding: 2.5rem 4rem 0;
  display: flex;
  align-items: center;
  gap: 1.2rem;
  font-family: 'Times New Roman', serif;

  .text {
    font-size: 1.4rem;
    font-family: 'Times New Roman', serif;
    font-weight: 800;
    letter-spacing: 3px;
    text-transform: uppercase;
    color: #7c3aed;
    white-space: nowrap;
  }

  &::before, &::after {
    content: '';
    flex: 1;
    height: 1px;
    background: linear-gradient(to right, transparent, rgba(55, 48, 163, 0.18), transparent);
  }

  @media (max-width: 768px) { padding: 2rem 1.5rem 0; }
`;

/* ─── Strength Grid ─── */
const Grid = styled.div`
  position: relative;
  z-index: 1;
  padding: 2rem 3rem 5rem;
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1px;
  background: rgba(55, 48, 163, 0.08);
  font-family: 'Times New Roman', serif;

  @media (max-width: 1100px) { grid-template-columns: repeat(3, 1fr); }
  @media (max-width: 720px)  { grid-template-columns: repeat(2, 1fr); padding: 1.25rem 1rem 2.5rem; }
`;

const Tile = styled.div`
  background: #ffffff;
  padding: 1.8rem 1.6rem;
  position: relative;
  overflow: hidden;
  cursor: default;
  transition: background 0.3s ease;
  font-family: 'Times New Roman', serif;

  &::before {
    content: '';
    position: absolute;
    top: 0; left: 0; right: 0;
    height: 3px;
    background: ${p => p.accent || '#3730a3'};
    transform: scaleX(0);
    transform-origin: left;
    transition: transform 0.4s cubic-bezier(0.22, 1, 0.36, 1);
  }

  &:hover {
    background: ${p => p.softBg || '#f5f3ff'};
    &::before { transform: scaleX(1); }
    .tile-icon { color: ${p => p.accent || '#3730a3'}; transform: scale(1.2) rotate(-5deg); }
    .tile-title { color: ${p => p.accent || '#3730a3'}; }
  }

  @media (max-width: 720px) { padding: 1.4rem 1.2rem; }
`;

const TileIcon = styled.div`
  font-size: 1.45rem;
  color: #c0bde8;
  margin-bottom: 0.9rem;
  transition: color 0.3s, transform 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
`;

const TileTitle = styled.h4`
  font-family: 'Times New Roman', serif;
  font-size: 1.1rem;
  font-weight: 800;
  color: #1a1a2e;
  margin: 0 0 0.55rem;
  transition: color 0.3s;
`;

const TileDesc = styled.p`
  font-family: 'Times New Roman', serif;
  font-size: 0.9rem;
  color: #7070a0;
  line-height: 1.6;
  margin: 0;
`;

/* ─── Data ─── */
const strengths = [
  { icon: <FaChalkboardTeacher />, title: "Unique Technique",       desc: "Proven four-step methodology — Explain, Show, Do & Review",               accent: '#7c3aed', softBg: '#f5f3ff' },
  { icon: <FaUserTie />,           title: "Renowned Trainers",      desc: "Learn from the best with decades of combined industry experience",         accent: '#0d9488', softBg: '#f0fdf9' },
  { icon: <FaUsers />,             title: "Personalized Coaching",  desc: "Curriculum tailored to different learning styles & requirements",          accent: '#2563eb', softBg: '#eff6ff' },
  { icon: <FaBookOpen />,          title: "Relevant Content",       desc: "Current industry-standard training with latest coding guidelines",         accent: '#d97706', softBg: '#fffbeb' },
  { icon: <FaTrophy />,            title: "Proven Track Record",    desc: "5000+ candidates trained with 98% certification success rate",             accent: '#dc2626', softBg: '#fef2f2' },
  { icon: <FaLaptopCode />,        title: "Comprehensive Training", desc: "Covering ICD-10 CM, CPT, ICD-10 PCS with hands-on practice",              accent: '#059669', softBg: '#ecfdf5' },
  { icon: <FaHospital />,          title: "Experienced Faculty",    desc: "Real-world hospital and physician practice experience",                    accent: '#0891b2', softBg: '#ecfeff' },
  { icon: <FaHeadphones />,        title: "Audio/Video Coaching",   desc: "Complex concepts taught with multimedia illustrations",                   accent: '#4f46e5', softBg: '#eef2ff' },
  { icon: <FaComments />,          title: "Approachable Trainers",  desc: "24/7 forum access for coding concept assistance",                         accent: '#9333ea', softBg: '#faf5ff' },
  { icon: <FaChartLine />,         title: "Regular Counseling",     desc: "Personal guidance on underperformance & exam prep",                       accent: '#e11d48', softBg: '#fff1f2' },
  { icon: <FaBuilding />,          title: "Structured Environment", desc: "Replicates on-the-job experience for day-one readiness",                  accent: '#0d9488', softBg: '#f0fdf9' },
  { icon: <FaClipboardCheck />,    title: "Pre & Post Evaluations", desc: "Comprehensive testing to identify & fill knowledge gaps",                 accent: '#2563eb', softBg: '#eff6ff' },
];

/* ─── Component ─── */
export default function StrengthsSection() {
  return (
    <Section>
      <HeroBand>
        <HeroImagePanel>
          <img src="/images/methodology.png" alt="Learn Thru Us Methodology" />
        </HeroImagePanel>

        <HeroCopyPanel>
          <EyebrowTag><span /> Why Choose Us</EyebrowTag>

          <HeroTitle>
            Built on<br />
            <em>Strengths</em> that<br />
            Deliver Results
          </HeroTitle>

          <HeroSub>
            What makes Learn Thru Us Academy the preferred choice for
            medical coding training — from faculty to methodology to proven outcomes.
          </HeroSub>
        </HeroCopyPanel>
      </HeroBand>

      <DividerRow>
        <span className="text">Our 12 Core Strengths</span>
      </DividerRow>

      <Grid>
        {strengths.map((s, i) => (
          <Tile key={i} accent={s.accent} softBg={s.softBg}>
            <TileIcon className="tile-icon">{s.icon}</TileIcon>
            <TileTitle className="tile-title">{s.title}</TileTitle>
            <TileDesc>{s.desc}</TileDesc>
          </Tile>
        ))}
      </Grid>
    </Section>
  );
}